// 图鉴 selectors（EP-008-S2 / GDD 03 §②-2.2 / ②-4 / ⑤；主架构 §5.2）。
//
// 完成度分母 / 完成度 / 排序全部由存档 + 礼物目录**派生**，不落第二份真相源。
// 解锁状态由 inventory 派生（!some(s => s.giftId === id)），不写盘。
//
// 注意：本文件位于 src/domain，不得调用 Date.now / Math.random（CI 守门）。
import type { InventoryStackV1 } from '../../save/types';
import { GIFTS } from '../gifts/giftDef';

// 图鉴分母 = enabled 母题数（GDD 03 §⑤）。
// 必须 = 10（v2 起明信片不再是礼物，已从礼物池移出 → 11 → 10；**不含** v1.1 预留母题，否则会变 15）。
// ⚠️ 本行注释曾写「必须 = 11」，与 giftDef.ts 的实际 enabled 数量和下方断言都不符，属过时注释，
//   已于 P6-POLISH-ENG-002 修正为 10 —— 不要再照抄旧注释。
// 见 tests/unit/domain/space/inventory.test.ts 的 B1 双向断言。
export const catalogDenominator = GIFTS.filter((g) => g.enabled).length;

// 已收集 enabled 母题数（去重母题，非 stack 数）。
export function ownedEnabledCount(inventory: InventoryStackV1[]): number {
  const owned = new Set(inventory.map((s) => s.giftId));
  return GIFTS.filter((g) => g.enabled && owned.has(g.id)).length;
}

// 完成度（GDD 03 §②-4）：owned / denominator。
export function completion(inventory: InventoryStackV1[]): { owned: number; denominator: number } {
  return { owned: ownedEnabledCount(inventory), denominator: catalogDenominator };
}

// 🆕 P6-POLISH-ENG-002（设计规格 07 §D / 文案 06 §4）：主页顶部进度文案。
//   「拾起」是设计侧内部术语（"从地上捡"），老板反馈看不懂；换成「带回」（小狗带东西回家，一眼就懂）。
//   只反映**礼物**进度，不含明信片（明信片有自己的进度载体，混进来会让"它到底在说什么"更不清楚）。
//   B1 硬约束：五档全中文、**零阿拉伯数字**，可以给精确数字的场景交给空间页。
export function progressText(owned: number, denominator: number): string {
  if (owned <= 0) return '还没带回';
  if (denominator > 0 && owned >= denominator) return '都在这儿了';
  const ratio = denominator > 0 ? owned / denominator : 0;
  if (ratio < 0.35) return '带回一些了'; // 1–3 / 10
  if (ratio < 0.7) return '带回不少了'; // 4–6 / 10
  return '快摆满了'; // 7–9 / 10
}

// 排序（GDD 03 §②-2.2）：firstObtainedAt 升序；同值按 giftId 字典序（稳定）。
export function sortStacks(inventory: InventoryStackV1[]): InventoryStackV1[] {
  return [...inventory].sort((a, b) => {
    if (a.firstObtainedAt !== b.firstObtainedAt) return a.firstObtainedAt - b.firstObtainedAt;
    if (a.giftId < b.giftId) return -1;
    if (a.giftId > b.giftId) return 1;
    return 0;
  });
}
